import React, {useEffect, useMemo, useState} from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  withStyles,
} from '@material-ui/core';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import styles from './styles';
import PropTypes from 'prop-types';
import { Portlet, PortletContent, TableCellWithConfig } from 'components';
import { displayModes } from 'services/enums/displayModes';
import classNames from 'classnames';
import Columns from '../columns';
import TemplateGroup from '../components/TemplateGroup';
import { getTemplateGroupContent, getUnassignedTemplateStopsGroupContent } from '../helper';

function TemplateStopSelectionTable(props) {
  const {
    classes,
    className,
    t,
    templates,
    unassignedTemplateStops,
    selectedUnassignedTemplateStops,
    selectedAssignedTemplateStops,
    onSelectAssignedTemplateStops,
    onSelectUnassignedTemplateStops,
    columnConfigs,
  } = props;

  const rootClassName = classNames(classes.root, className);

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => template.stops.length > 0);
  }, [templates]);
  
  function toggleExpanded(id) {
    setCollapsedTemplates((prevState) => {
      const newState = { ...prevState };
      newState[id] = !newState[id];
      return newState;
    });
  }

  // object that contains the expanded state of each template and unassigned
  const [collapsedTemplates, setCollapsedTemplates] = useState({});

  useEffect(() => {
    const result = filteredTemplates.reduce(
      (acc, template) => {
        acc[template.id] = true;
        return acc;
      },
      { unassigned: true }
    );
    setCollapsedTemplates(result);
  }, [filteredTemplates]);

  function selectTemplate(template) {
    // unselect all template stops if all are selected, otherwise select all remaining unselected
    const unselectedTemplateStops = template.stops.filter(
      (stop) =>
        selectedAssignedTemplateStops.findIndex((c) => c.id === stop.id) < 0
    );
    if (unselectedTemplateStops.length === 0) {
      onSelectAssignedTemplateStops({
        templateStops: template.stops,
        unselect: true,
      });
    } else {
      onSelectAssignedTemplateStops({
        templateStops: unselectedTemplateStops,
      });
    }
  }

  function selectUnassignedTemplateStops() {
    // unselect all template stops if all are selected, otherwise select all remaining unselected
    if (
      selectedUnassignedTemplateStops.length === unassignedTemplateStops.length
    ) {
      onSelectUnassignedTemplateStops({
        templateStops: unassignedTemplateStops,
        unselect: true,
      });
    } else {
      onSelectUnassignedTemplateStops({
        templateStops:
          selectedUnassignedTemplateStops.length === 0
            ? unassignedTemplateStops
            : unassignedTemplateStops.filter(
                (stop) =>
                  selectedUnassignedTemplateStops.findIndex(
                    (c) => c.id === stop.id
                  ) < 0
              ),
      });
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <Portlet className={rootClassName}>
        <PortletContent noPadding>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columnConfigs.map((columnConfig) => (
                  <TableCellWithConfig
                    className={classes.tableHeader}
                    config={columnConfig}
                    key={columnConfig.order}
                    width={columnConfig.width}
                  >
                    {Columns[columnConfig.columnName].header({
                      t,
                    })}
                  </TableCellWithConfig>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TemplateGroup
                isCollapsed={collapsedTemplates['unassigned']}
                onSelectStop={onSelectUnassignedTemplateStops}
                onSelectGroup={selectUnassignedTemplateStops}
                selectedStops={selectedUnassignedTemplateStops}
                stops={unassignedTemplateStops}
                shouldStopsHover={true}
                toggleCollapsed={(e) => {
                  e.stopPropagation();
                  toggleExpanded('unassigned');
                }}
                columnConfigs={columnConfigs}
                groupContent={getUnassignedTemplateStopsGroupContent(
                  unassignedTemplateStops
                )}
                templateSelected={
                  !unassignedTemplateStops.some(
                    (stop) =>
                      selectedUnassignedTemplateStops.findIndex(
                        (c) => c.id === stop.id
                      ) < 0
                  )
                }
              />
              {filteredTemplates.map((template) => (
                <TemplateGroup
                  key={template.id}
                  isCollapsed={collapsedTemplates[template.id]}
                  onSelectStop={onSelectAssignedTemplateStops}
                  onSelectGroup={() => selectTemplate(template)}
                  selectedStops={selectedAssignedTemplateStops}
                  stops={template.stops}
                  shouldStopsHover={true}
                  toggleCollapsed={(e) => {
                    e.stopPropagation();
                    toggleExpanded(template.id);
                  }}
                  columnConfigs={columnConfigs}
                  template={template}
                  templateSelected={
                    !template.stops.some(
                      (stop) =>
                        selectedAssignedTemplateStops.findIndex(
                          (c) => c.id === stop.id
                        ) < 0
                    )
                  }
                  groupContent={getTemplateGroupContent(template)}
                />
              ))}
            </TableBody>
          </Table>
        </PortletContent>
      </Portlet>
    </div>
  );
}

TemplateStopSelectionTable.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  displayMode: PropTypes.oneOf(Object.values(displayModes)).isRequired,
  templates: PropTypes.array.isRequired,
  unassignedTemplateStops: PropTypes.array.isRequired,
  selectedUnassignedTemplateStops: PropTypes.array.isRequired,
  selectedAssignedTemplateStops: PropTypes.array.isRequired,
  onSelectAssignedTemplateStops: PropTypes.func.isRequired,
  onSelectUnassignedTemplateStops: PropTypes.func.isRequired,
  columnConfigs: PropTypes.array.isRequired,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(TemplateStopSelectionTable);
